import { DateString, Uuid } from '@/api/types';
import {
  DispositionReasonType,
  DispositionSpecificationType,
  TurndownOutcome,
} from '@/api/apis/case/status/case-outcome.types';
import { RandomizationResultType } from '@/api/apis/case/case.types';

// Enums

export enum OrganType {
  Liver = 'Liver',
  Heart = 'Heart',
  Lungs = 'Lungs',
  Kidney = 'Kidney',
}

/**
 * TMDXLP-14091: Kidneys must be hidden from Schedule. Same members as `OrganType` minus `Kidney`,
 * values are real `OrganType` members so it drops into any `OrganType`-typed list/prop unchanged.
 * To bring Kidneys back in a given Schedule call site, swap `OrganTypeWithoutKidney` back to `OrganType`.
 */
export const OrganTypeWithoutKidney = {
  Liver: OrganType.Liver,
  Heart: OrganType.Heart,
  Lungs: OrganType.Lungs,
} satisfies Partial<Record<keyof typeof OrganType, OrganType>>;

export enum NRPOption {
  Yes = 'Yes',
  No = 'No',
}

export enum NRPType {
  none = 'None',
  aNrp = 'A',
  taNrp = 'TA',
}

export type BaseMediaFile = {
  fileId: Uuid;
  fileName: string;
  path: string;
  duration?: number;
  mediaType: 'Photo' | 'Video';
};

export type ClinicalMediaFile = BaseMediaFile & {
  details?: string;
  preview?: BaseMediaFile;
};

export enum ClinicalItemType {
  // Timestamp
  NRPStart = 'NRPStart',
  ScheduledDonorORStartTime = 'ScheduledDonorORStartTime',
  DonorInOR = 'DonorInOR',
  RecordDelay = 'RecordDelay',
  Heparin = 'Heparin',
  SkinIncisionTime = 'SkinIncisionTime',
  CrossClampTimeFlushTime = 'CrossClampTimeFlushTime',
  FinalFlush = 'FinalFlush',
  DonorDidNotExpireInTime = 'DonorDidNotExpireInTime',
  DryRun = 'DryRun',
  OCSDeviceDeployment = 'OCSDeviceDeployment',
  DeployedToDonorHospital = 'DeployedToDonorHospital',
  AtDonorHospital = 'AtDonorHospital',
  OrganOnOCS = 'OrganOnOCS',
  OrganOnSCS = 'OrganOnSCS',
  LeavingDonorHospital = 'LeavingDonorHospital',
  ArrivalAtRecipientHospital = 'ArrivalAtRecipientHospital',
  OrganAccepted = 'OrganAccepted',
  LeavingRecipientHospital = 'LeavingRecipientHospital',
  SpecialistIsDone = 'SpecialistIsDone',
  PFOValveCondition = 'PFOValveCondition',
  OffOCS = 'OffOCS',
  OffSCS = 'OffSCS',
  Priming = 'Priming',
  CardiacArrest = 'CardiacArrest',
  PreOCSFlush = 'PreOCSFlush',
  AtHub = 'AtHub',
  CheckInOCSDevice = 'CheckInOCSDevice',
  WithdrawalOfLifeSupportTime = 'WithdrawalOfLifeSupportTime',
  // Timestamp with custom data fields
  Randomization = 'Randomization',
  OrganApproved = 'OrganApproved',
  OrganDeclined = 'OrganDeclined',
  // YesNo
  BloodAvailabilityCheck = 'BloodAvailabilityCheck',
  DonorLabelsCheck = 'DonorLabelsCheck',
  BronchCartAndSuctionCheck = 'BronchCartAndSuctionCheck',
  ReintubationPersonalCheck = 'ReintubationPersonalCheck',
  OnSiteCoordinatorsContacts = 'OnSiteCoordinatorsContacts',
  PickUpOfOrganLabelAndTissueSamples = 'PickUpOfOrganLabelAndTissueSamples',
  IceForTheOCSCoolerPriorDeparture = 'IceForTheOCSCoolerPriorDeparture',
  CysticDuct = 'CysticDuct',
  ResearchBloodSamples = 'ResearchBloodSamples',
  TeamHuddle = 'TeamHuddle',
  SupplementalOrgan = 'SupplementalOrgan',
  Bile = 'Bile',

  WithdrawalOfLifeSupport = 'WithdrawalOfLifeSupport',
  NRPApplicabilityAndType = 'NRPApplicabilityAndType',
  // Photo
  DeathDeclarationTime = 'DeathDeclarationTime',
  PostFlushLungsPhoto = 'PostFlushLungsPhoto',
  Stabilization = 'Stabilization',
  PreOCSBloodSample = 'PreOCSBloodSample',
  BloodSample = 'BloodSample',
  LiverOnABackBench = 'LiverOnABackBench',
  AnatomyPhotos = 'AnatomyPhotos',
  // Video
  PostFlushBronch = 'PostFlushBronch',
  BronchInSitu = 'BronchInSitu',
  BronchVideoOnTheOCS = 'BronchVideoOnTheOCS',
  PFRatioInSitu = 'PFRatioInSitu',
  PFRatioOnOCSDonorHospital = 'PFRatioOnOCSDonorHospital',
  PFRatioOnOCSTXCenter = 'PFRatioOnOCSTXCenter',
  FinalBronchVideoAtTheRecipientHospital = 'FinalBronchVideoAtTheRecipientHospital',
  OffOCSPhotoAndVideo = 'OffOCSPhotoAndVideo',
  OnOCSVideo = 'OnOCSVideo',
  InSituVideoOfHeart = 'InSituVideoOfHeart',
  // PhotoAndVideo
  VisualInspection = 'VisualInspection',
  FinalPhysicalExamPictureOrVideo = 'FinalPhysicalExamPictureOrVideo',
  FinalCheck = 'FinalCheck',
  ReconstructionVideosPhotos = 'ReconstructionVideosPhotos',
  OrganMonitoring = 'OrganMonitoring',
  MultipleDonorSpecimenHandoff = 'MultipleDonorSpecimenHandoff',
  // YesNoMedia
  DonorBiopsyReport = 'DonorBiopsyReport',
  TxOCSBiopsy = 'TxOCSBiopsy',
  LiverReconstructionQuestionnaire = 'LiverReconstructionQuestionnaire',
  LiverAnatomyQuestionnaire = 'LiverAnatomyQuestionnaire',
  VitalUpdates = 'VitalUpdates',
  BloodCollection = 'BloodCollection',
  // Heart - Cooling and Flush / Beating Heart
  HeartPassOption = 'HeartPassOption',
  PerfusionTerminationOption = 'PerfusionTerminationOption',
  ScsTerminationOption = 'SCSTerminationOption',
  DonorSpecimenHandoff = 'DonorSpecimenHandoff',
  ServiceConfirmation = 'ServiceConfirmation',
  DiscardConfirmationForm = 'DiscardConfirmationForm',
  Turndown = 'Turndown',
  OneLungTurndown = 'OneLungTurndown',
  // Grant Access
  IdentityAccessTicketGranted = 'IdentityAccessTicketGranted',
  IdentityAccessTicketRevoked = 'IdentityAccessTicketRevoked',
}

export enum RadiometerType {
  GC4Plus = 'GC4Plus',
  GC8Plus = 'GC8Plus',
  DonorRadiometer = 'DonorRadiometer',
}

export enum BloodFromType {
  LeftAtrium = 'LeftAtrium',
  ArterialLine = 'ArterialLine',
  PulmonaryVeins = 'PulmonaryVeins',
}

export enum SupplementalOrganType {
  Kidney = 'Kidney',
  Pancreas = 'Pancreas',
}

export enum ProvidedServiceType {
  SurgicalRetrieval = 'SurgicalRetrieval',
  OCSManagement = 'OCSManagement',
  OCSPerfusionSet = 'OCSPerfusionSet',
}

export enum LiverAnatomyType {
  Normal = 'Normal',
  LGA = 'LGA',
  Aorta = 'Aorta',
  RightSMA = 'RightSMA',
  Complex = 'Complex',
  SMA = 'SMA',
  Other = 'Other',
}

export enum HeartPassOptionType {
  CoolingAndFlush = 'CoolingAndFlush',
  BeatingHeart = 'BeatingHeart',
}

export enum InventoryPartType {
  LiverPerfusionSet = 'LiverPerfusionSet',
  LiverBileSaltsSet = 'LiverBileSaltsSet',
  LiverGasCylinder80PercentOxygen = 'LiverGasCylinder80PercentOxygen',
  HeartPerfusionSet = 'HeartPerfusionSet',
  KidneyPerfusionSet = 'KidneyPerfusionSet',
  HeartSolutionSetRef1300 = 'HeartSolutionSetRef1300',
  CableTieToolSmallDiameter = 'CableTieToolSmallDiameter',
  GasCylinderEuUsCanada = 'GasCylinderEuUsCanada',
  LungPerfusionSet = 'LungPerfusionSet',
  LungSolutionRef2300 = 'LungSolutionRef2300',
  GasCylinderM15A = 'GasCylinderM15A',
  GasCylinderLu = 'GasCylinderLu',
}

export enum WOLSType {
  OR = 'OR',
  PACU = 'PACU',
  ICU = 'ICU',
  Other = 'Other',
}

export enum PerfusionTerminationType {
  doubleLungFlush = 'doubleLungFlush',
  firstLungFlush = 'firstLungFlush',
  secondLungFlush = 'secondLungFlush',
  sequentialLungFlush = 'sequentialLungFlush',
}

// Item's specific data types

export type TurndownData = {
  dispositionReason?: DispositionReasonType;
  dispositionSpecification?: DispositionSpecificationType;
  turndownOutcome?: TurndownOutcome;
  lung?: 'Left' | 'Right';
};

export type RandomizationDetails = {
  result?: RandomizationResultType;
  txCenterId?: Uuid;
  txCenterRepresentativeName?: string;
};

export type OrganApprovalData = {
  approverFirstName: string;
  approverLastName: string;
  approverUserId: Uuid;
};

export type OrganDeclineData = {
  declinerFirstName: string;
  declinerLastName: string;
  declinerUserId: Uuid;
  reason: DispositionSpecificationType;
};

export type RadiometerFirstSection = {
  temperature?: number;
  type?: string;
  pH?: number;
  pcO2?: number;
  paO2?: number;
  pvO2?: number;
  sO2?: number;
  bEecf?: number;
  hcO3?: number;
  tcO2?: number;
  lac?: number;
};

export type RadiometerSecondSection = {
  temperature: number;
  pH: number;
  pcO2: number;
  pO2: number;
};

export type RadiometerThirdSection = {
  na: number;
  k: number;
  iCA: number;
  glu: number;
  hct: number;
  hb: number;
};

export type Radiometer = {
  firstSection: RadiometerFirstSection;
  secondSection?: RadiometerSecondSection;
  thirdSection?: RadiometerThirdSection;
};

export type VitalParameters = {
  heartRate?: number;
  systolicBloodPressure?: number;
  diastolicBloodPressure?: number;
  meanArterialPressure?: number;
  oxygenSaturation?: number;
  respiratoryRate?: number;
  readingTimeInMinutes: number;
};

export type PulmonaryVeinData = {
  radiometer: Radiometer;
  radiometerType: RadiometerType;
  mediaFiles: ClinicalMediaFile[];
};

export type TimestampWithZone = {
  timestamp: DateString;
  timeZone: string;
};

export type PerfusionTerminationOption = {
  [PerfusionTerminationType.doubleLungFlush]?: {
    flushTime: TimestampWithZone;
  };
  [PerfusionTerminationType.sequentialLungFlush]?: {
    firstLungFlushTime?: TimestampWithZone;
    secondLungFlushTime?: TimestampWithZone;
  };
};

export type SpecialistSignature = {
  fullName: string;
  credentials: string;
  file: ClinicalMediaFile;
};

export type ServiceSigner = {
  photoPath: string;
  signature: SpecialistSignature;
  timestamp: DateString;
  timeZone: string;
};

export type Inventory = {
  id: Uuid;
  partNumber: string;
  serialCode: string;
  type: InventoryPartType;
};

export type ClinicalServiceConfirmation = {
  retrievalSurgery?: {
    surgeryDate: string;
    surgeryDateTimeZone: string;
    unosId: string;
    organType: OrganType;
    donorHospital: {
      name: string;
      logoPath: string;
    };
  };
  retrievalSurgeon?: ServiceSigner;
  ocsManagement?: {
    serviceDate: string;
    serviceDateTimeZone: string;
    serviceOutcomeType: string;
    txCenter: {
      name: string;
      logoPath: string;
    };
  };
  ocsSpecialist?: ServiceSigner;
  txRepresentative?: ServiceSigner;
  providedServiceTypes?: ProvidedServiceType[];
  ocsSupplies?: {
    ocsConsole: {
      id: Uuid;
      sn: string;
      fullSN: string;
      locationOfOrigin: string;
    };
    inventory: Inventory[];
  };
};

export type DiscardConfirmationType = {
  unosId?: string;
  nurse?: ServiceSigner;
  organType?: OrganType;
  ocsSpecialist?: ServiceSigner;
  txCenter?: { name: string };
  opoFullName?: string;
};

export type BloodCollection = {
  volume?: number;
};

export type DonorSpecimenHandoff = {
  signature?: SpecialistSignature;
};

export type PfRatioData = {
  pfRatio?: number;
  averagePFRatio?: number;
  vt?: number;
  peep?: number;
  bloodFrom?: BloodFromType;
  lupvpfRatio?: number;
  lupv?: PulmonaryVeinData;
  llpvpfRatio?: number;
  llpv?: PulmonaryVeinData;
  rupvpfRatio?: number;
  rupv?: PulmonaryVeinData;
  rlpvpfRatio?: number;
  rlpv?: PulmonaryVeinData;
};

export type HeartPassOption = {
  heartPassOptionType?: HeartPassOptionType;
};

export type VitalsData = {
  vitalParameters?: VitalParameters;
};

export type RadiometerData = {
  radiometer?: Radiometer;
  radiometerType?: RadiometerType;
};

export type SupplementalOrganModalData = {
  organ?: SupplementalOrganType;
};

export type NrpTypeData = {
  nrpType?: NRPType;
};

export type LiverAnatomyQuestionnaireData = {
  answer: LiverAnatomyType;
  text?: string;
};

export type LiverReconstructionQuestionnaireData = {
  answer?: string;
  text?: string;
};

export type WithdrawalOfLifeSupportData = {
  answer: WOLSType;
  text?: string;
};

// Generic Data structures

export type BaseClinicalDataItem = {
  id: Uuid;
  timestamp: DateString;
  timeZone: string;
  details?: string;
  mediaFiles?: ClinicalMediaFile[];
  yesNo?: boolean;
};

export type SingleRecord<TType extends ClinicalItemType, TData = unknown> = {
  clinicalItemType: TType;
  item: BaseClinicalDataItem & TData;
  items?: never;
};

export type MultiRecord<TType extends ClinicalItemType, TData = unknown> = {
  clinicalItemType: TType;
  item?: never;
  items: (BaseClinicalDataItem & TData)[];
};

export type DataFromSingleRecord<T> =
  T extends SingleRecord<ClinicalItemType, infer D> ? BaseClinicalDataItem & D : never;

export type DataFromMultiRecord<T> =
  T extends MultiRecord<ClinicalItemType, infer D> ? BaseClinicalDataItem & D : never;

export type DataRecordItemType<T> = DataFromSingleRecord<T> | DataFromMultiRecord<T>;

// Clinical records

// Single item records
type SingleBaseItemTypes =
  | ClinicalItemType.NRPStart
  | ClinicalItemType.ScheduledDonorORStartTime
  | ClinicalItemType.DonorInOR
  | ClinicalItemType.Heparin
  | ClinicalItemType.SkinIncisionTime
  | ClinicalItemType.CrossClampTimeFlushTime
  | ClinicalItemType.FinalFlush
  | ClinicalItemType.DonorDidNotExpireInTime
  | ClinicalItemType.OCSDeviceDeployment
  | ClinicalItemType.DeployedToDonorHospital
  | ClinicalItemType.AtDonorHospital
  | ClinicalItemType.OrganOnOCS
  | ClinicalItemType.OrganOnSCS
  | ClinicalItemType.LeavingDonorHospital
  | ClinicalItemType.ArrivalAtRecipientHospital
  | ClinicalItemType.OrganAccepted
  | ClinicalItemType.LeavingRecipientHospital
  | ClinicalItemType.SpecialistIsDone
  | ClinicalItemType.PFOValveCondition
  | ClinicalItemType.OffOCS
  | ClinicalItemType.OffSCS
  | ClinicalItemType.Priming
  | ClinicalItemType.CardiacArrest
  | ClinicalItemType.PreOCSFlush
  | ClinicalItemType.AtHub
  | ClinicalItemType.CheckInOCSDevice
  | ClinicalItemType.WithdrawalOfLifeSupportTime
  | ClinicalItemType.IdentityAccessTicketGranted
  | ClinicalItemType.IdentityAccessTicketRevoked
  | ClinicalItemType.DeathDeclarationTime
  | ClinicalItemType.BloodAvailabilityCheck
  | ClinicalItemType.DonorLabelsCheck
  | ClinicalItemType.BronchCartAndSuctionCheck
  | ClinicalItemType.ReintubationPersonalCheck
  | ClinicalItemType.OnSiteCoordinatorsContacts
  | ClinicalItemType.PickUpOfOrganLabelAndTissueSamples
  | ClinicalItemType.IceForTheOCSCoolerPriorDeparture
  | ClinicalItemType.CysticDuct
  | ClinicalItemType.ResearchBloodSamples
  | ClinicalItemType.TeamHuddle
  | ClinicalItemType.PostFlushLungsPhoto
  | ClinicalItemType.LiverOnABackBench
  | ClinicalItemType.AnatomyPhotos
  | ClinicalItemType.PostFlushBronch
  | ClinicalItemType.BronchInSitu
  | ClinicalItemType.BronchVideoOnTheOCS
  | ClinicalItemType.FinalBronchVideoAtTheRecipientHospital
  | ClinicalItemType.OffOCSPhotoAndVideo
  | ClinicalItemType.FinalPhysicalExamPictureOrVideo
  | ClinicalItemType.FinalCheck
  | ClinicalItemType.ReconstructionVideosPhotos
  | ClinicalItemType.DonorBiopsyReport
  | ClinicalItemType.TxOCSBiopsy;

export type SingleGenericClinicalRecord = SingleRecord<SingleBaseItemTypes>;
export type OrganApprovedRecord = SingleRecord<ClinicalItemType.OrganApproved, OrganApprovalData>;
export type OrganDeclinedRecord = SingleRecord<ClinicalItemType.OrganDeclined, OrganDeclineData>;
export type RandomizationRecord = SingleRecord<ClinicalItemType.Randomization, RandomizationDetails>;
export type TurndownRecord = SingleRecord<
  ClinicalItemType.DryRun | ClinicalItemType.Turndown | ClinicalItemType.OneLungTurndown,
  TurndownData
>;
export type ServiceConfirmationRecord = SingleRecord<ClinicalItemType.ServiceConfirmation, ClinicalServiceConfirmation>;
export type BloodCollectionRecord = SingleRecord<ClinicalItemType.BloodCollection, BloodCollection>;
export type DonorSpecimenHandoffRecord = SingleRecord<ClinicalItemType.DonorSpecimenHandoff, DonorSpecimenHandoff>;
export type PfRatioSingleRecord = SingleRecord<ClinicalItemType.PFRatioInSitu, PfRatioData & RadiometerData>;

export type HeartPassOptionRecord = SingleRecord<ClinicalItemType.HeartPassOption, HeartPassOption>;
export type PerfusionTerminationRecord = SingleRecord<
  ClinicalItemType.PerfusionTerminationOption | ClinicalItemType.ScsTerminationOption,
  PerfusionTerminationOption
>;
export type DiscardConfirmationRecord = SingleRecord<ClinicalItemType.DiscardConfirmationForm, DiscardConfirmationType>;
export type NrpApplicabilityRecord = SingleRecord<ClinicalItemType.NRPApplicabilityAndType, NrpTypeData>;
export type LiverAnatomyQuestionnaireRecord = SingleRecord<
  ClinicalItemType.LiverAnatomyQuestionnaire,
  LiverAnatomyQuestionnaireData
>;
export type LiverReconstructionQuestionnaireRecord = SingleRecord<
  ClinicalItemType.LiverReconstructionQuestionnaire,
  LiverReconstructionQuestionnaireData
>;
export type WithdrawalOfLifeSupportRecord = SingleRecord<
  ClinicalItemType.WithdrawalOfLifeSupport,
  WithdrawalOfLifeSupportData
>;

// Multiple items records
type MultipleBaseItemTypes =
  | ClinicalItemType.OrganMonitoring
  | ClinicalItemType.RecordDelay
  | ClinicalItemType.InSituVideoOfHeart
  | ClinicalItemType.OnOCSVideo
  | ClinicalItemType.Stabilization
  | ClinicalItemType.VisualInspection
  | ClinicalItemType.Bile;

export type MultipleGenericClinicalRecord = MultiRecord<MultipleBaseItemTypes>;
export type SupplementalOrganRecord = MultiRecord<ClinicalItemType.SupplementalOrgan, SupplementalOrganModalData>;
export type MultipleDonorSpecimenHandoffRecord = MultiRecord<
  ClinicalItemType.MultipleDonorSpecimenHandoff,
  DonorSpecimenHandoff
>;
export type VitalUpdatesRecord = MultiRecord<ClinicalItemType.VitalUpdates, VitalsData>;
export type BloodSampleRecord = MultiRecord<
  ClinicalItemType.PreOCSBloodSample | ClinicalItemType.BloodSample,
  RadiometerData
>;
export type PfRatioMultiRecord = MultiRecord<
  ClinicalItemType.PFRatioOnOCSDonorHospital | ClinicalItemType.PFRatioOnOCSTXCenter,
  PfRatioData & RadiometerData
>;

export type ClinicalDataRecord =
  | OrganApprovedRecord
  | OrganDeclinedRecord
  | RandomizationRecord
  | TurndownRecord
  | ServiceConfirmationRecord
  | BloodCollectionRecord
  | DonorSpecimenHandoffRecord
  | MultipleDonorSpecimenHandoffRecord
  | PfRatioSingleRecord
  | PfRatioMultiRecord
  | HeartPassOptionRecord
  | PerfusionTerminationRecord
  | DiscardConfirmationRecord
  | SupplementalOrganRecord
  | NrpApplicabilityRecord
  | VitalUpdatesRecord
  | BloodSampleRecord
  | LiverAnatomyQuestionnaireRecord
  | LiverReconstructionQuestionnaireRecord
  | WithdrawalOfLifeSupportRecord
  | SingleGenericClinicalRecord
  | MultipleGenericClinicalRecord;

export type ClinicalDataItem = DataRecordItemType<ClinicalDataRecord>;

export type ClinicalData = ClinicalDataRecord[];

// Other

export type OffOcsType = {
  item: TimestampWithZone | PerfusionTerminationOption;
  clinicalItemType: ClinicalItemType;
};

export type ClinicalMediaType = {
  file: ClinicalMediaFile;
  timestamp?: string;
  timeZone?: string;
  ocsTime?: string;
};
